import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, LogOut, Video, Eye, EyeOff } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVideosPage = () => {
  const navigate = useNavigate();
  const { requireAuth, logout, getAuthHeaders } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [videos, setVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    youtube_url: "",
    description: "",
    thumbnail_url: "",
    order: 0
  });

  useEffect(() => {
    if (!requireAuth()) return;
    fetchVideos();
  }, [requireAuth]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos`, {
        headers: getAuthHeaders()
      });
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error("Erreur lors du chargement des vidéos", error);
      if (error.response?.status === 403) {
        toast.error("Session expirée. Veuillez vous reconnecter.");
        logout();
      }
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const extractYouTubeId = (url) => {
    // Extract YouTube video ID from various URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const videoId = extractYouTubeId(formData.youtube_url);
      const payload = {
        ...formData,
        youtube_url: videoId,
        thumbnail_url: formData.thumbnail_url || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      };

      if (editingVideo) {
        await axios.put(`${API}/admin/videos/${editingVideo.video_id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success("Vidéo mise à jour avec succès!");
      } else {
        await axios.post(`${API}/admin/videos`, payload, {
          headers: getAuthHeaders()
        });
        toast.success("Vidéo ajoutée avec succès!");
      }

      setFormData({ title: "", youtube_url: "", description: "", thumbnail_url: "", order: 0 });
      setEditingVideo(null);
      await fetchVideos();
    } catch (error) {
      const message = error?.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      youtube_url: video.youtube_url,
      description: video.description || "",
      thumbnail_url: video.thumbnail_url || "",
      order: video.order || 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (videoId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette vidéo?")) return;

    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Vidéo supprimée");
      await fetchVideos();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleActive = async (video) => {
    try {
      await axios.put(`${API}/admin/videos/${video.video_id}`, {
        is_active: !video.is_active
      }, {
        headers: getAuthHeaders()
      });
      toast.success(video.is_active ? "Vidéo masquée" : "Vidéo activée");
      await fetchVideos();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin/editor")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Vidéos YouTube</h1>
          </div>
          <Button variant="outline" onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            Déconnexion
          </Button>
        </div>

        {/* Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              {editingVideo ? "Modifier la vidéo" : "Ajouter une nouvelle vidéo"}
            </CardTitle>
            <CardDescription>
              Prix d'accès aux vidéos: <strong>3 USD (6,000 CDF)</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre de la vidéo *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="Exemple: Introduction au Mystère de la Croix"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="youtube_url">
                  URL YouTube ou ID de la vidéo *
                </Label>
                <Input
                  id="youtube_url"
                  value={formData.youtube_url}
                  onChange={(e) => handleChange("youtube_url", e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... ou dQw4w9WgXcQ"
                  required
                />
                <p className="text-sm text-gray-500">
                  Formats acceptés: URL complète YouTube, lien court youtu.be, ou ID de la vidéo
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optionnel)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Description de la vidéo..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="order">Ordre d'affichage</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => handleChange("order", parseInt(e.target.value))}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="thumbnail_url">URL de l'image (optionnel)</Label>
                  <Input
                    id="thumbnail_url"
                    value={formData.thumbnail_url}
                    onChange={(e) => handleChange("thumbnail_url", e.target.value)}
                    placeholder="Laissez vide pour auto"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={loading} className="flex-1">
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? "Enregistrement..." : editingVideo ? "Mettre à jour" : "Ajouter la vidéo"}
                </Button>
                {editingVideo && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingVideo(null);
                      setFormData({ title: "", youtube_url: "", description: "", thumbnail_url: "", order: 0 });
                    }}
                  >
                    Annuler
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Videos List */}
        <Card>
          <CardHeader>
            <CardTitle>Vidéos ({videos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {videos.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                Aucune vidéo. Ajoutez votre première vidéo ci-dessus!
              </p>
            ) : (
              <div className="space-y-4">
                {videos.map((video) => (
                  <div
                    key={video.video_id}
                    className={`border rounded-lg p-4 ${video.is_active ? 'bg-white' : 'bg-gray-100'}`}
                  >
                    <div className="flex gap-4">
                      <img
                        src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_url}/maxresdefault.jpg`}
                        alt={video.title}
                        className="w-40 h-24 object-cover rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{video.title}</h3>
                            <p className="text-sm text-gray-600">{video.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              ID: {video.youtube_url} | Ordre: {video.order}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleActive(video)}
                            >
                              {video.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(video)}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(video.video_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminVideosPage;
